/**
 * TimeTrackerProvider
 *
 * Tracks user activity across different areas of the application.
 * Records page visits, time spent, and broadcasts activity via WebSocket.
 */

import { useQueryClient } from '@tanstack/react-query'
import { AllItems, KEY as BOOTSTRAP_KEY } from 'api/aws/organizations_table/useAllItemsBootstrap'
import { useActiveUser } from 'api/slack/useActiveUser'
import { useWebsocketContext } from 'context/Websocket/WebsocketProvider'
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { PageVisit, RouteAreaConfig, TimeTrackerContextValue, TimeTrackerState, UserActivityEvent } from './types'

/**
 * Route configuration for mapping paths to areas
 */
const ROUTE_CONFIGS: RouteAreaConfig[] = [
  { path: '/organizations', area: 'Organizations', hasEntityPages: true },
  { path: '/deals', area: 'Deals', hasEntityPages: true },
  { path: '/contacts', area: 'Contacts', hasEntityPages: true },
  { path: '/products', area: 'Products', hasEntityPages: true },
  { path: '/tasks', area: 'Tasks', hasEntityPages: true },
  { path: '/projects', area: 'Projects', hasEntityPages: true },
  { path: '/dashboard', area: 'Dashboard' },
  { path: '/boards', area: 'Boards' },
  { path: '/metrics', area: 'Metrics' },
  { path: '/nco', area: 'NCO', hasEntityPages: true },
  { path: '/mtb', area: 'Monthly Task Board' },
  { path: '/approvals', area: 'Approvals' },
  { path: '/strategy', area: 'Strategy', hasEntityPages: true },
  { path: '/documents', area: 'Documents' },
  { path: '/timeline', area: 'Timeline' },
  { path: '/teamtasks', area: 'Team Tasks', hasEntityPages: true },
  { path: '/leads', area: 'Leads' },
]

/**
 * Generate a simple UUID for session/visit IDs
 */
const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Format duration in human-readable format
 */
const formatDuration = (ms: number): string => {
  if (ms < 1000) return '0s'

  const seconds = Math.floor(ms / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)

  if (hours > 0) {
    const remainingMinutes = minutes % 60
    return `${hours}h ${remainingMinutes}m`
  }
  if (minutes > 0) {
    const remainingSeconds = seconds % 60
    return `${minutes}m ${remainingSeconds}s`
  }
  return `${seconds}s`
}

/**
 * Parsed route info with additional task context
 */
interface ParsedRoute {
  area: string
  entityId?: string
  relatedOrgId?: string
  taskId?: string
}

/**
 * Normalize a path for comparison purposes
 * This strips tab segments from routes like MTB where tabs don't represent different "visits"
 * e.g., /mtb/ORG_xxx/main and /mtb/ORG_xxx/seo should be treated as the same visit
 */
const normalizePathForTracking = (path: string): string => {
  const parts = path.split('/').filter(Boolean)

  // For MTB routes: /mtb/{ORG_PK}/{tab} -> normalize to /mtb/{ORG_PK}
  // Tab changes within the same org should not create new visits
  if (parts[0] === 'mtb' && parts.length >= 2) {
    // Return just /mtb/ORG_xxx, ignoring the tab segment
    return `/${parts[0]}/${parts[1]}`
  }

  // For NCO routes: /nco/{ORG_PK}/{DEAL_SK}/{tab} -> normalize to /nco/{ORG_PK}/{DEAL_SK}
  if (parts[0] === 'nco' && parts.length >= 3) {
    return `/${parts[0]}/${parts[1]}/${parts[2]}`
  }

  // For teamtasks: /teamtasks/{ORG_PK}/{TASK_SK}/{tab} -> normalize to /teamtasks/{ORG_PK}/{TASK_SK}
  if (parts[0] === 'teamtasks' && parts.length >= 3) {
    return `/${parts[0]}/${parts[1]}/${parts[2]}`
  }

  return path
}

/**
 * Extract area and entity info from a path
 * Special handling for teamtasks: /teamtasks/{ORG_PK}/{TASK_SK}/...
 */
const parseRoute = (path: string): ParsedRoute => {
  // Find matching route config
  const config = ROUTE_CONFIGS.find(rc => path.startsWith(rc.path))

  if (!config) {
    return { area: 'Other' }
  }

  const parts = path.split('/').filter(Boolean)

  // Special handling for teamtasks route: /teamtasks/{ORG_PK}/{TASK_SK}/...
  if (config.path === '/teamtasks' && parts.length >= 3) {
    const orgPK = parts[1] // e.g., ORG_02467855-a8fa-4b38-b569-d5f5b1b3044e
    const taskSK = parts[2] // e.g., TASK_mj0in3ry_6n0g4o

    return {
      area: config.area,
      entityId: taskSK, // The task SK is the main entity being viewed
      relatedOrgId: orgPK, // The org PK is the related organization
      taskId: taskSK,
    }
  }

  // Extract entity ID if this route has entity pages
  let entityId: string | undefined
  if (config.hasEntityPages && parts.length >= 2) {
    entityId = parts[1] // e.g., ORG_123, DEAL_456
  }

  return { area: config.area, entityId }
}

/**
 * LocalStorage key for widget visibility preference
 */
const WIDGET_VISIBILITY_KEY = 'timeTracker.widgetVisible'

/**
 * Default context value
 */
const defaultState: TimeTrackerState = {
  sessionId: '',
  currentVisit: null,
  visits: [],
  totalSessionTime: 0,
  areaBreakdown: {},
  isTracking: false,
  sessionStartedAt: 0,
  isWidgetVisible: false,
}

const TimeTrackerContext = createContext<TimeTrackerContextValue | undefined>(undefined)

export const TimeTrackerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const location = useLocation()
  const queryClient = useQueryClient()
  const { activeUser, isActiveUserLoading } = useActiveUser()
  const { ws } = useWebsocketContext()

  // State - initialize widget visibility from localStorage
  const [state, setState] = useState<TimeTrackerState>(() => {
    let savedVisibility = false
    try {
      const saved = localStorage.getItem(WIDGET_VISIBILITY_KEY)
      savedVisibility = saved === 'true'
    } catch {
      // Ignore localStorage errors
    }
    return {
      ...defaultState,
      sessionId: generateId(),
      sessionStartedAt: Date.now(),
      isTracking: true,
      isWidgetVisible: savedVisibility,
    }
  })

  // Refs for tracking without causing re-renders
  const currentVisitRef = useRef<PageVisit | null>(null)
  const lastPathRef = useRef<string>('')

  /**
   * Get user info from active user
   */
  const userInfo = useMemo(() => {
    const user = activeUser?.user ?? {}
    return {
      userId: user.id ?? '',
      userName: user.real_name ?? user.name ?? '',
    }
  }, [activeUser])

  /**
   * Look up entity name from query cache
   */
  const getEntityName = useCallback(
    (entityId: string | undefined, area: string): string | undefined => {
      if (!entityId) return undefined

      try {
        // Get cached bootstrap data
        const data = queryClient.getQueryData<AllItems>(BOOTSTRAP_KEY)
        if (!data) return undefined

        // Look up based on area
        switch (area) {
          case 'Organizations':
          case 'NCO': {
            const org = data.organizations?.find(o => o.org_uuid === entityId || o.PK === entityId || o.SK === entityId)
            return org?.name || org?.organization_name
          }
          case 'Deals': {
            const deal = data.deals?.find(d => d.deal_uuid === entityId || d.PK === entityId || d.SK === entityId)
            return deal?.name || deal?.deal_name
          }
          case 'Products': {
            const product = data.products?.find(
              p => p.product_uuid === entityId || p.PK === entityId || p.SK === entityId
            )
            return product?.name || product?.productName
          }
          default:
            return undefined
        }
      } catch (e) {
        console.warn('[TimeTracker] Failed to lookup entity name:', e)
        return undefined
      }
    },
    [queryClient]
  )

  /**
   * Look up task info from query cache
   * Returns task name, status, and related org name
   */
  const getTaskInfo = useCallback(
    (
      taskId: string | undefined,
      relatedOrgId: string | undefined
    ): {
      taskName?: string
      taskStatus?: string
      orgName?: string
    } => {
      if (!taskId) return {}

      try {
        // Get cached tasks data
        const tasks = queryClient.getQueryData<any[]>(['tasks_table'])
        const task = tasks?.find(t => t.SK === taskId || t.task_uuid === taskId)

        // Get org name if we have a related org
        let orgName: string | undefined
        if (relatedOrgId) {
          const data = queryClient.getQueryData<AllItems>(BOOTSTRAP_KEY)
          const org = data?.organizations?.find(o => o.PK === relatedOrgId || o.org_uuid === relatedOrgId)
          orgName = org?.name || org?.organization_name
        }

        return {
          taskName: task?.name,
          taskStatus: task?.masterStatus,
          orgName,
        }
      } catch (e) {
        console.warn('[TimeTracker] Failed to lookup task info:', e)
        return {}
      }
    },
    [queryClient]
  )

  /**
   * Broadcast activity event via WebSocket
   */
  const broadcastActivity = useCallback(
    (eventType: UserActivityEvent['type'], visit: PageVisit) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) return

      const event: UserActivityEvent = {
        type: eventType,
        userId: visit.userId,
        userName: visit.userName,
        area: visit.area,
        entityId: visit.entityId,
        entityName: visit.entityName,
        relatedOrgId: visit.relatedOrgId,
        taskStatus: visit.taskStatus,
        taskId: visit.taskId,
        path: visit.path,
        timestamp: Date.now(),
        duration: visit.duration,
        sessionId: visit.sessionId,
      }

      ws.send(JSON.stringify(event))
    },
    [ws]
  )

  /**
   * Close the current visit and record duration
   */
  const closeCurrentVisit = useCallback(() => {
    const current = currentVisitRef.current
    if (!current) return null

    const now = Date.now()
    const duration = now - current.enteredAt
    const closedVisit: PageVisit = {
      ...current,
      exitedAt: now,
      duration,
    }

    // Update state with completed visit
    setState(prev => ({
      ...prev,
      visits: [...prev.visits, closedVisit],
      totalSessionTime: prev.totalSessionTime + duration,
      areaBreakdown: {
        ...prev.areaBreakdown,
        [closedVisit.area]: (prev.areaBreakdown[closedVisit.area] || 0) + duration,
      },
    }))

    // Broadcast exit event
    broadcastActivity('user.page_exit', closedVisit)

    currentVisitRef.current = null
    return closedVisit
  }, [broadcastActivity])

  /**
   * Open a new visit for the current path
   */
  const openNewVisit = useCallback(
    (path: string) => {
      if (!userInfo.userId || !state.isTracking) return

      const { area, entityId, relatedOrgId, taskId } = parseRoute(path)

      // Debug logging for Team Tasks
      // if (area === 'Team Tasks') {
      //   console.log('[TimeTracker] Team Tasks parseRoute result:', { path, area, entityId, relatedOrgId, taskId })
      // }

      // Get entity name based on area
      let entityName = getEntityName(entityId, area)
      let taskStatus: string | undefined
      let finalRelatedOrgId = relatedOrgId

      // For Team Tasks, look up task info and use org name as entity name context
      if (area === 'Team Tasks' && taskId) {
        const taskInfo = getTaskInfo(taskId, relatedOrgId)
        entityName = taskInfo.taskName || entityName
        taskStatus = taskInfo.taskStatus
      }

      const newVisit: PageVisit = {
        id: generateId(),
        userId: userInfo.userId,
        userName: userInfo.userName,
        path,
        area,
        entityId,
        entityName,
        relatedOrgId: finalRelatedOrgId,
        taskStatus,
        taskId,
        enteredAt: Date.now(),
        sessionId: state.sessionId,
      }

      currentVisitRef.current = newVisit
      setState(prev => ({
        ...prev,
        currentVisit: newVisit,
      }))

      // Broadcast enter event
      broadcastActivity('user.page_enter', newVisit)
    },
    [userInfo, state.isTracking, state.sessionId, getEntityName, getTaskInfo, broadcastActivity]
  )

  /**
   * Handle route changes
   * Uses normalized paths to avoid treating tab changes as new visits
   */
  useEffect(() => {
    const currentPath = location.pathname
    const normalizedCurrent = normalizePathForTracking(currentPath)
    const normalizedLast = normalizePathForTracking(lastPathRef.current)

    // Skip if same normalized path or not tracking
    // This prevents tab changes within MTB/NCO/teamtasks from creating new visits
    if (normalizedCurrent === normalizedLast || !state.isTracking) return

    // Close previous visit if exists
    closeCurrentVisit()

    // Open new visit
    openNewVisit(currentPath)

    lastPathRef.current = currentPath
  }, [location.pathname, state.isTracking, closeCurrentVisit, openNewVisit])

  /**
   * Initialize tracking when user info becomes available
   */
  useEffect(() => {
    if (!isActiveUserLoading && userInfo.userId && !currentVisitRef.current) {
      openNewVisit(location.pathname)
      lastPathRef.current = location.pathname
    }
  }, [isActiveUserLoading, userInfo.userId, location.pathname, openNewVisit])

  /**
   * Cleanup on unmount - close current visit
   */
  useEffect(() => {
    return () => {
      closeCurrentVisit()
    }
  }, [closeCurrentVisit])

  /**
   * Handle visibility change (tab switching)
   */
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden - could pause tracking or mark visit
        if (currentVisitRef.current) {
          broadcastActivity('user.activity_update', {
            ...currentVisitRef.current,
            duration: Date.now() - currentVisitRef.current.enteredAt,
          })
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [broadcastActivity])

  /**
   * Start tracking
   */
  const startTracking = useCallback(() => {
    setState(prev => ({ ...prev, isTracking: true }))
    if (location.pathname && !currentVisitRef.current) {
      openNewVisit(location.pathname)
    }
  }, [location.pathname, openNewVisit])

  /**
   * Stop tracking
   */
  const stopTracking = useCallback(() => {
    closeCurrentVisit()
    setState(prev => ({ ...prev, isTracking: false, currentVisit: null }))
  }, [closeCurrentVisit])

  /**
   * Get current page duration (live)
   */
  const getCurrentPageDuration = useCallback((): number => {
    const current = currentVisitRef.current
    if (!current) return 0
    return Date.now() - current.enteredAt
  }, [])

  /**
   * Get formatted current page duration
   */
  const getFormattedCurrentPageDuration = useCallback((): string => {
    return formatDuration(getCurrentPageDuration())
  }, [getCurrentPageDuration])

  /**
   * Get area with most time spent
   */
  const getMostVisitedArea = useCallback((): { area: string; duration: number } | null => {
    const breakdown = state.areaBreakdown
    const entries = Object.entries(breakdown)
    if (entries.length === 0) return null

    const sorted = entries.sort((a, b) => b[1] - a[1])
    return { area: sorted[0][0], duration: sorted[0][1] }
  }, [state.areaBreakdown])

  /**
   * Show the widget
   */
  const showWidget = useCallback(() => {
    setState(prev => ({ ...prev, isWidgetVisible: true }))
    try {
      localStorage.setItem(WIDGET_VISIBILITY_KEY, 'true')
    } catch {
      // Ignore localStorage errors
    }
  }, [])

  /**
   * Hide the widget
   */
  const hideWidget = useCallback(() => {
    setState(prev => ({ ...prev, isWidgetVisible: false }))
    try {
      localStorage.setItem(WIDGET_VISIBILITY_KEY, 'false')
    } catch {
      // Ignore localStorage errors
    }
  }, [])

  /**
   * Toggle widget visibility
   */
  const toggleWidget = useCallback(() => {
    setState(prev => {
      const newVisibility = !prev.isWidgetVisible
      try {
        localStorage.setItem(WIDGET_VISIBILITY_KEY, String(newVisibility))
      } catch {
        // Ignore localStorage errors
      }
      return { ...prev, isWidgetVisible: newVisibility }
    })
  }, [])

  /**
   * Context value
   */
  const value = useMemo<TimeTrackerContextValue>(
    () => ({
      ...state,
      startTracking,
      stopTracking,
      getFormattedDuration: formatDuration,
      getCurrentPageDuration,
      getFormattedCurrentPageDuration,
      getMostVisitedArea,
      showWidget,
      hideWidget,
      toggleWidget,
    }),
    [
      state,
      startTracking,
      stopTracking,
      getCurrentPageDuration,
      getFormattedCurrentPageDuration,
      getMostVisitedArea,
      showWidget,
      hideWidget,
      toggleWidget,
    ]
  )

  return <TimeTrackerContext.Provider value={value}>{children}</TimeTrackerContext.Provider>
}

export const useTimeTrackerContext = () => {
  const ctx = React.useContext(TimeTrackerContext)
  if (!ctx) {
    throw new Error('useTimeTrackerContext must be used within TimeTrackerProvider')
  }
  return ctx
}

export default TimeTrackerProvider
